import { useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Wifi } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOfflineCache } from "./useOfflineCache";
import { products } from "@/data/products";

interface SyncCallback {
  name: string;
  sync: () => Promise<void>;
}

/**
 * Hook for background sync when connection is restored
 */
export function useBackgroundSync() {
  const syncCallbacksRef = useRef<SyncCallback[]>([]);
  const isSyncingRef = useRef(false);
  const gigsCache = useOfflineCache<any>("gigs");
  const productsCache = useOfflineCache<any>("products");

  // Register a sync callback
  const registerSync = useCallback((name: string, sync: () => Promise<void>) => {
    syncCallbacksRef.current = [
      ...syncCallbacksRef.current.filter((cb) => cb.name !== name),
      { name, sync },
    ];
  }, []);

  // Unregister a sync callback
  const unregisterSync = useCallback((name: string) => {
    syncCallbacksRef.current = syncCallbacksRef.current.filter(
      (cb) => cb.name !== name
    );
  }, []);

  // Sync all registered callbacks
  const syncAll = useCallback(async () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    try {
      // Sync gigs/livestream events
      if (gigsCache.isReady) {
        const { data } = await supabase
          .from("livestream_events")
          .select("id, title, event_date, ticket_price, status")
          .in("status", ["upcoming", "live"])
          .order("event_date", { ascending: true })
          .limit(10);

        if (data) {
          await gigsCache.cacheData(data);
        }
      }

      // Sync products
      if (productsCache.isReady) {
        await productsCache.cacheData(products);
      }

      // Run any additional registered sync callbacks
      for (const callback of syncCallbacksRef.current) {
        try {
          await callback.sync();
        } catch (e) {
          console.error(`Sync failed for ${callback.name}:`, e);
        }
      }

      toast.success("Data synced", {
        description: "Your cached content has been updated",
        duration: 3000,
      });
    } catch (e) {
      console.error("Background sync failed:", e);
    } finally {
      isSyncingRef.current = false;
    }
  }, [gigsCache, productsCache]);

  // Listen for online event and trigger sync
  useEffect(() => {
    let wasOffline = !navigator.onLine;

    const handleOnline = () => {
      if (wasOffline) {
        // Small delay to ensure connection is stable
        setTimeout(() => {
          if (navigator.onLine) {
            syncAll();
          }
        }, 2000);
      }
      wasOffline = false;
    };

    const handleOffline = () => {
      wasOffline = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [syncAll]);

  // Initial sync on mount if online
  useEffect(() => {
    if (navigator.onLine && gigsCache.isReady && productsCache.isReady) {
      // Debounce initial sync
      const timer = setTimeout(() => {
        syncAll();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [gigsCache.isReady, productsCache.isReady, syncAll]);

  return {
    registerSync,
    unregisterSync,
    syncNow: syncAll,
  };
}
